import { Button } from '@/components/ui/button';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Form } from 'react-bootstrap';

const REPORTS_PERMISSIONS_GROUP = [
  'sitrep:create',
  'sitrep:draft',
  'sitrep:approve',
];
const MAP_LAYERS_PERMISSIONS_GROUP = [
  'internalLayer:create',
  'internalLayer:propose',
  'internalLayer:approve',
];
const NOTIFICATIONS_PERMISSIONS_GROUP = [
  'notification:create',
  'notification:propose',
  'notification:approve',
];

const INCIDENT_SELF_PERMISSIONS_GROUP = [
  'incident:activateSelf',
  'incident:checkInSelf',
];
const INCIDENT_CARDS_PERMISSIONS_GROUP = ['incident:editCards'];
const INCIDENT_RESOURCE_REQUEST_PERMISSIONS_GROUP = ['resourceRequest:create'];
const INCIDENT_MANAGEMENT_PERMISSIONS_GROUP = [
  'incident:changeRoster',
  // 'incident:proposeRosterChange', // disabled until implemented
  // 'incident:approveRosterChange',
  'incident:activateMember',
  'incident:generateIap',
  'incident:advanceOperationalPeriod',
  'incident:editSettings',
];
const INCIDENT_AI_TOOLING_PERMISSIONS_GROUP = ['incident:accessAi'];
const INCIDENT_VIEW_PERMISSIONS_GROUP = ['incident:viewFuturePeriod'];

const GROUP_MANAGEMENT_PERMISSIONS_GROUP = [
  'Open or Close Group',
  'Edit Group Name',
  'org:changeMembers',
];
const GROUP_ROLES_PERMISSIONS_GROUP = [
  'Create Role',
  'Delete Role',
  'Edit Role',
  'Assign Member to Role',
  'Unassign Member from Role',
];
const GROUP_INCIDENT_PERMISSIONS_GROUP = [
  'incident:create',
  'Open or Close Incident',
];
const GROUP_RESOURCE_SET_PERMISSIONS_GROUP = ['resourceSet:edit'];

const PERMISSION_GROUPS = {
  'Organization Management': GROUP_MANAGEMENT_PERMISSIONS_GROUP,
  Roles: GROUP_ROLES_PERMISSIONS_GROUP,
  Incident: GROUP_INCIDENT_PERMISSIONS_GROUP,
  'Incident Self': INCIDENT_SELF_PERMISSIONS_GROUP,
  Reports: REPORTS_PERMISSIONS_GROUP,
  'Map Layers': MAP_LAYERS_PERMISSIONS_GROUP,
  Resources: GROUP_RESOURCE_SET_PERMISSIONS_GROUP,
  Notifications: NOTIFICATIONS_PERMISSIONS_GROUP,
  'Incident Cards': INCIDENT_CARDS_PERMISSIONS_GROUP,
  'Resource Requests': INCIDENT_RESOURCE_REQUEST_PERMISSIONS_GROUP,
  'Incident Management': INCIDENT_MANAGEMENT_PERMISSIONS_GROUP,
  'AI & Tooling': INCIDENT_AI_TOOLING_PERMISSIONS_GROUP,
  'Incident View': INCIDENT_VIEW_PERMISSIONS_GROUP,
};

function getPermissionGroups(permissions: PermissionOption[]) {
  const groups: { [key: string]: PermissionOption[] } = {};

  Object.entries(PERMISSION_GROUPS).forEach(([groupName, groupPermissions]) => {
    const groupPermissionsFiltered: PermissionOption[] = [];

    groupPermissions.forEach((groupPermission) => {
      const matchingPermission = permissions.find(
        (perm) => perm.action === groupPermission
      );
      if (matchingPermission) {
        groupPermissionsFiltered.push(matchingPermission);
      }
    });

    if (groupPermissionsFiltered.length > 0) {
      groups[groupName] = groupPermissionsFiltered;
    }
  });

  return groups;
}

export type PermissionOption = {
  id: string;
  title: string;
  description: string;
  action: string;
};

export function permissionsAfterBulkToggle(
  current: PermissionOption[],
  visible: PermissionOption[],
  on: boolean
): PermissionOption[] {
  const visibleIds = new Set(visible.map((perm) => perm.id));
  const kept = current.filter((perm) => !visibleIds.has(perm.id));
  return on ? [...kept, ...visible] : kept;
}

export interface EditRolePermissionsProps {
  permissionsOptions: PermissionOption[];
  currentPermissions: PermissionOption[];
  onPermissionChange: (permissions: PermissionOption[]) => void;
}

const EditRolePermissions: React.FC<EditRolePermissionsProps> = ({
  currentPermissions,
  onPermissionChange,
  permissionsOptions,
}) => {
  const permissionGroups = getPermissionGroups(permissionsOptions);
  const visiblePermissions = Object.values(permissionGroups).flat();
  const visibleIds = new Set(visiblePermissions.map((perm) => perm.id));
  const visibleOnCount = currentPermissions.filter((perm) =>
    visibleIds.has(perm.id)
  ).length;
  const allVisibleOn =
    visiblePermissions.length > 0 &&
    visibleOnCount === visiblePermissions.length;

  const turnAllVisible = (on: boolean) => {
    onPermissionChange(
      permissionsAfterBulkToggle(currentPermissions, visiblePermissions, on)
    );
  };

  return (
    <div>
      <h5>Permissions</h5>
      <div className="d-flex flex-row align-items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={allVisibleOn}
          onClick={() => turnAllVisible(true)}
        >
          Turn all on
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={visibleOnCount === 0}
          onClick={() => turnAllVisible(false)}
        >
          Turn all off
        </Button>
      </div>
      <hr className="mt-1 mb-3" />
      <div>
        {Object.keys(permissionGroups).map((groupName, index) => (
          <div key={groupName}>
            {groupName === 'Incident Self Permissions' ? null : (
              <h6 className="mt-3">{groupName}</h6>
            )}
            <div className="d-flex flex-row align-items-center justify-content-between flex-wrap">
              {permissionGroups[groupName].map((option) => (
                <div
                  key={option.id}
                  className="d-flex flex-row align-items-center justify-content-between mb-3"
                  style={{
                    minWidth: '23vw',
                  }}
                >
                  <div className="d-flex flex-row align-items-center gap-1">
                    <SharedIcon
                      iconName="info"
                      size="sm"
                      color="primary"
                      title={option.description}
                      aria-label={option.description}
                      style={{ cursor: 'default' }}
                    />
                    <span>{option.title}</span>
                  </div>
                  <Form.Switch
                    id={`permission-toggle-${option.id}`}
                    checked={currentPermissions.some(
                      (perm) => perm.id === option.id
                    )}
                    onChange={() => {
                      if (
                        currentPermissions.some((perm) => perm.id === option.id)
                      ) {
                        onPermissionChange(
                          currentPermissions.filter(
                            (perm) => perm.id !== option.id
                          )
                        );
                      } else {
                        onPermissionChange([...currentPermissions, option]);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
            {index !== Object.keys(permissionGroups).length - 1 && (
              <hr
                className="mb-1 mt-1 ms-0 me-0"
                style={{
                  width: '100%',
                  opacity: 0.5,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EditRolePermissions;
